import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, CheckCircle2, Link2, Linkedin, Loader2, Mail, MessageCircle, Radar, Save, Send, Share2, ShieldCheck, Twitter } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];
const CATEGORIES = ["social", "shopping", "streaming", "tech", "finance", "travel", "telecom", "gaming", "dating", "lifestyle"];
// Vérification des fuites HIBP : masquée tant que la clé HIBP_API_KEY n'est pas configurée. Passer à true pour réactiver.
const SHOW_BREACH_CHECK = false;

export default function Discover() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(location.state?.user || null);
  const [checking, setChecking] = useState(!location.state?.user);
  const [services, setServices] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [breach, setBreach] = useState(null);
  const [breachLoading, setBreachLoading] = useState(false);
  const [inbox, setInbox] = useState({
    gmail: { status: null, scanning: false, result: null },
    outlook: { status: null, scanning: false, result: null },
  });
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (location.state?.user) return;
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => setUser(r.data))
      .catch(() => navigate("/"))
      .finally(() => setChecking(false));
  }, [location.state, navigate]);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/gdpr/services`, { withCredentials: true }).then((r) => setServices(r.data.services)).catch(() => {});
    axios.get(`${API}/discover`, { withCredentials: true }).then((r) => setSelected(new Set(r.data.service_ids || []))).catch(() => {});
    ["gmail", "outlook"].forEach((p) => {
      axios.get(`${API}/${p}/status`, { withCredentials: true })
        .then((r) => setInbox((prev) => ({ ...prev, [p]: { ...prev[p], status: r.data } })))
        .catch(() => {});
    });
  }, [user]);

  useEffect(() => {
    ["gmail", "outlook"].forEach((p) => {
      const param = searchParams.get(p);
      if (param === "connected") {
        toast.success(t(`discover.${p}ConnectedToast`));
        setSearchParams({}, { replace: true });
        axios.get(`${API}/${p}/status`, { withCredentials: true })
          .then((r) => setInbox((prev) => ({ ...prev, [p]: { ...prev[p], status: r.data } })))
          .catch(() => {});
      } else if (param === "error") {
        toast.error(t("discover.inboxError"));
        setSearchParams({}, { replace: true });
      }
    });
  }, [searchParams, setSearchParams, t]);

  const patchInbox = (p, patch) => setInbox((prev) => ({ ...prev, [p]: { ...prev[p], ...patch } }));

  const scanInbox = async (p) => {
    if (inbox[p].scanning) return;
    patchInbox(p, { scanning: true, result: null });
    try {
      const { data } = await axios.post(`${API}/${p}/scan`, {}, { withCredentials: true });
      patchInbox(p, { result: data });
    } catch (e) {
      toast.error(t("discover.inboxError"));
    } finally {
      patchInbox(p, { scanning: false });
    }
  };

  const disconnectInbox = async (p) => {
    try {
      await axios.post(`${API}/${p}/disconnect`, {}, { withCredentials: true });
      patchInbox(p, { status: { ...inbox[p].status, connected: false, email: null }, result: null });
      toast.success(t("discover.inboxDisconnected"));
    } catch (e) {
      toast.error(t("discover.inboxError"));
    }
  };

  const addInboxResults = (p) => {
    const result = inbox[p].result;
    if (!result) return;
    setSelected((prev) => {
      const next = new Set(prev);
      result.services.forEach((s) => next.add(s.id));
      return next;
    });
    toast.success(t("discover.inboxAdded"));
  };

  const shareResultUrl = (p) => {
    const r = inbox[p].result;
    return `${process.env.REACT_APP_BACKEND_URL}/api/og/scan?n=${r ? r.services.length : 0}`;
  };
  const shareResultText = (p) => {
    const r = inbox[p].result;
    return r ? t("discover.inboxShareText", { count: r.services.length }) : "";
  };
  const shareResultLinks = (p) => {
    const text = `${shareResultText(p)} ${shareResultUrl(p)}`;
    return [
      { id: "x", icon: Twitter, href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}` },
      { id: "linkedin", icon: Linkedin, href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareResultUrl(p))}` },
      { id: "whatsapp", icon: MessageCircle, href: `https://wa.me/?text=${encodeURIComponent(text)}` },
    ];
  };
  const copyResultShare = async (p) => {
    try {
      await navigator.clipboard.writeText(`${shareResultText(p)} ${shareResultUrl(p)}`);
      toast.success(t("blog.shareCopied"));
    } catch (e) {
      // clipboard unavailable
    }
  };
  const shareResultNative = async (p) => {
    if (navigator.share) {
      try {
        await navigator.share({ text: shareResultText(p), url: shareResultUrl(p) });
      } catch (e) {
        // user dismissed the share sheet
      }
    } else {
      copyResultShare(p);
    }
  };

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const checkBreaches = async () => {
    setBreachLoading(true);
    setBreach(null);
    try {
      const { data } = await axios.get(`${API}/me/breaches`, { withCredentials: true });
      setBreach({ found: data.found, list: data.breaches });
    } catch (e) {
      setBreach({ error: e.response?.status === 503 ? "not_configured" : "error" });
    } finally {
      setBreachLoading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/discover`, { service_ids: [...selected] }, { withCredentials: true });
      toast.success(t("discover.saved"));
    } catch (e) {
      toast.error(t("discover.sendError"));
    } finally {
      setSaving(false);
    }
  };

  const sendAll = async () => {
    if (selected.size === 0 || sending) return;
    setSending(true);
    try {
      const ids = [...selected];
      for (let i = 0; i < ids.length; i += 10) {
        await axios.post(`${API}/gdpr/requests`, { service_ids: ids.slice(i, i + 10), type: "access" }, { withCredentials: true });
      }
      await save();
      toast.success(t("discover.sendAllDone", { count: ids.length }));
    } catch (e) {
      toast.error(t("discover.sendError"));
    } finally {
      setSending(false);
    }
  };

  if (checking) {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-mint animate-spin" strokeWidth={1.5} />
      </main>
    );
  }

  return (
    <main data-testid="discover-page">
      <Header />
      <div className="pt-32 pb-24 max-w-4xl mx-auto px-6 lg:px-10 min-h-screen">
        <Link to="/account" data-testid="discover-back-link" className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-mint transition-colors duration-200">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          {t("discover.back")}
        </Link>

        <div className="mt-6 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-mint/10 border border-mint/20 flex items-center justify-center">
            <Radar className="w-6 h-6 text-mint" strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="font-display font-semibold tracking-tighter text-white text-3xl sm:text-4xl">{t("discover.title")}</h1>
            <p className="mt-1 text-sm text-white/50">{t("discover.subtitle")}</p>
          </div>
        </div>

        {SHOW_BREACH_CHECK && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }} className="mt-10 rounded-2xl border border-white/10 bg-card p-7">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="max-w-md">
              <h2 className="font-display font-semibold text-white text-lg">{t("discover.breachTitle")}</h2>
              <p className="mt-1 text-xs text-white/45 leading-relaxed">{t("discover.breachDesc")}</p>
            </div>
            <button
              onClick={checkBreaches}
              disabled={breachLoading}
              data-testid="breach-check-button"
              className="inline-flex items-center gap-2 rounded-full border border-mint/40 px-5 py-2.5 text-sm font-medium text-mint transition-colors duration-200 hover:bg-mint/10 disabled:opacity-50"
            >
              {breachLoading ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} /> : <ShieldCheck className="w-4 h-4" strokeWidth={1.5} />}
              {breachLoading ? t("discover.breachChecking") : t("discover.breachCta")}
            </button>
          </div>
          {breach && (
            <div data-testid="breach-result" className="mt-5 rounded-xl border border-white/8 bg-night/60 p-5">
              {breach.error === "not_configured" && <p className="text-sm text-white/50">{t("discover.breachNotConfigured")}</p>}
              {breach.error === "error" && <p className="text-sm text-red-300">{t("discover.breachError")}</p>}
              {!breach.error && !breach.found && (
                <p className="flex items-center gap-2 text-sm text-mint">
                  <CheckCircle2 className="w-4 h-4" strokeWidth={2} />
                  {t("discover.breachNone")}
                </p>
              )}
              {!breach.error && breach.found && (
                <div>
                  <p className="flex items-center gap-2 text-sm text-amber-300">
                    <AlertTriangle className="w-4 h-4" strokeWidth={2} />
                    {t("discover.breachFound", { count: breach.list.length })}
                  </p>
                  <ul className="mt-3 flex flex-wrap gap-2">
                    {breach.list.map((b) => (
                      <li key={b.Name} className="rounded-full bg-amber-400/10 border border-amber-400/25 px-3 py-1 text-xs text-amber-200">
                        {b.Title || b.Name}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </motion.div>
        )}

        {(inbox.gmail.status?.configured || inbox.outlook.status?.configured) && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }} className="mt-10 rounded-2xl border border-white/10 bg-card p-7" data-testid="inbox-scan-card">
          <div className="max-w-xl">
            <h2 className="font-display font-semibold text-white text-lg">{t("discover.inboxTitle")}</h2>
            <p className="mt-1 text-xs text-white/45 leading-relaxed">{t("discover.inboxDesc")}</p>
          </div>
          {["gmail", "outlook"].map((p) => inbox[p].status?.configured && (
            <div key={p} data-testid={`${p}-card`} className="mt-5 rounded-xl border border-white/8 bg-night/60 p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <span className="flex items-center gap-2 text-sm font-medium text-white/85">
                  <Mail className="w-4 h-4 text-mint" strokeWidth={1.5} />
                  {t(`discover.${p}Provider`)}
                </span>
                {!inbox[p].status.connected ? (
                  <a
                    href={`${API}/${p}/connect`}
                    data-testid={`${p}-connect-button`}
                    className="inline-flex items-center gap-2 rounded-full border border-mint/40 px-5 py-2 text-sm font-medium text-mint transition-colors duration-200 hover:bg-mint/10"
                  >
                    {t(`discover.${p}Connect`)}
                  </a>
                ) : (
                  <button
                    onClick={() => scanInbox(p)}
                    disabled={inbox[p].scanning}
                    data-testid={`${p}-scan-button`}
                    className="inline-flex items-center gap-2 rounded-full border border-mint/40 px-5 py-2 text-sm font-medium text-mint transition-colors duration-200 hover:bg-mint/10 disabled:opacity-50"
                  >
                    {inbox[p].scanning && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />}
                    {inbox[p].scanning ? t("discover.inboxScanning") : t("discover.inboxScan")}
                  </button>
                )}
              </div>
              {p === "gmail" && !inbox[p].status.connected && (
                <div data-testid="gmail-unverified-guide" className="mt-4 rounded-xl border border-amber-400/20 bg-amber-400/5 p-5 max-w-lg">
                  <p className="text-xs font-semibold text-amber-200/90">{t("discover.gmailGuideTitle")}</p>
                  <ol className="mt-3 space-y-2 text-[11px] text-white/55 leading-relaxed">
                    {[1, 2, 3, 4].map((step) => (
                      <li key={step} className="flex gap-2.5">
                        <span className="shrink-0 w-4.5 h-4.5 min-w-[18px] min-h-[18px] rounded-full bg-amber-400/15 text-amber-300 text-[10px] font-bold flex items-center justify-center">{step}</span>
                        <span>{t(`discover.gmailGuideStep${step}`)}</span>
                      </li>
                    ))}
                  </ol>
                  <p className="mt-3 pt-3 border-t border-amber-400/15 text-[11px] text-white/40 leading-relaxed">
                    {t("discover.gmailGuideSafe")}{" "}
                    <Link to="/blog/avertissement-google-wadoo" data-testid="gmail-guide-article-link" className="text-mint hover:underline underline-offset-2">
                      {t("discover.gmailGuideMore")}
                    </Link>
                  </p>
                </div>
              )}
              {inbox[p].status.connected && (
                <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-white/40">
                  <span data-testid={`${p}-connected-email`}>
                    {inbox[p].status.email ? t("discover.inboxConnectedAs", { email: inbox[p].status.email }) : t("discover.inboxConnected")}
                  </span>
                  <button onClick={() => disconnectInbox(p)} data-testid={`${p}-disconnect-button`} className="text-white/45 hover:text-red-300 underline underline-offset-2 transition-colors duration-200">
                    {t("discover.inboxDisconnect")}
                  </button>
                </div>
              )}
              {inbox[p].result && (
                <div data-testid={`${p}-result`} className="mt-4">
                  {inbox[p].result.services.length === 0 ? (
                    <p className="text-sm text-white/50">{t("discover.inboxNone", { count: inbox[p].result.scanned })}</p>
                  ) : (
                    <div>
                      <p data-testid={`${p}-result-count`} className="font-display font-semibold tracking-tighter text-mint text-4xl">{inbox[p].result.services.length}</p>
                      <p className="mt-1 text-sm text-mint">{t("discover.inboxResult", { count: inbox[p].result.services.length, scanned: inbox[p].result.scanned })}</p>
                      <ul className="mt-3 flex flex-wrap gap-2">
                        {inbox[p].result.services.map((s) => (
                          <li key={s.id} data-testid={`${p}-found-${s.id}`} className="rounded-full bg-mint/10 border border-mint/25 px-3 py-1 text-xs text-mint">
                            {s.name} · {s.emails}
                          </li>
                        ))}
                      </ul>
                      <button
                        onClick={() => addInboxResults(p)}
                        data-testid={`${p}-add-button`}
                        className="mt-4 inline-flex items-center gap-2 rounded-full bg-mint px-5 py-2.5 text-sm font-semibold text-night transition-transform duration-200 hover:scale-105"
                      >
                        <CheckCircle2 className="w-4 h-4" strokeWidth={2} />
                        {t("discover.inboxAdd")}
                      </button>
                      <p className="mt-2 text-[11px] text-white/35">{t("discover.inboxAddHint")}</p>
                      <div className="mt-5 pt-4 border-t border-white/8 flex flex-wrap items-center gap-3">
                        <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-white/40">{t("discover.inboxShareTitle")}</p>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => shareResultNative(p)}
                            data-testid={`${p}-share-native`}
                            className="w-8 h-8 rounded-full border border-white/15 flex items-center justify-center text-white/70 transition-colors duration-200 hover:border-mint/50 hover:text-mint"
                            aria-label="Share"
                          >
                            <Share2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                          </button>
                          {shareResultLinks(p).map((l) => (
                            <a
                              key={l.id}
                              href={l.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`${p}-share-${l.id}`}
                              className="w-8 h-8 rounded-full border border-white/15 flex items-center justify-center text-white/70 transition-colors duration-200 hover:border-mint/50 hover:text-mint"
                              aria-label={l.id}
                            >
                              <l.icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                            </a>
                          ))}
                          <button
                            onClick={() => copyResultShare(p)}
                            data-testid={`${p}-share-copy`}
                            className="w-8 h-8 rounded-full border border-white/15 flex items-center justify-center text-white/70 transition-colors duration-200 hover:border-mint/50 hover:text-mint"
                            aria-label="Copy"
                          >
                            <Link2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </motion.div>
        )}

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1, ease: EASE }} className="mt-12">
          <h2 className="font-display font-semibold text-white text-xl">{t("discover.checklistTitle")}</h2>
          <p className="mt-1 text-sm text-white/45">{t("discover.checklistHint")}</p>

          {selected.size > 0 && (
            <p data-testid="discover-counter" className="mt-4 inline-flex rounded-full bg-mint/10 border border-mint/25 px-4 py-1.5 text-sm text-mint font-medium">
              {t("discover.resultLine", { count: selected.size })}
            </p>
          )}

          {CATEGORIES.map((cat) => {
            const items = services.filter((s) => s.category === cat);
            if (items.length === 0) return null;
            return (
              <div key={cat} className="mt-8">
                <p className="text-xs font-medium uppercase tracking-[0.2em] text-white/40 mb-3">{t(`discover.categories.${cat}`)}</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {items.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => toggle(s.id)}
                      data-testid={`discover-service-${s.id}`}
                      className={`rounded-xl border px-4 py-3 text-sm text-left transition-colors duration-200 ${
                        selected.has(s.id) ? "border-mint/60 bg-mint/10 text-white" : "border-white/10 bg-card text-white/60 hover:border-white/25"
                      }`}
                    >
                      <span className="flex items-center justify-between gap-2">
                        {s.name}
                        {selected.has(s.id) && <CheckCircle2 className="w-4 h-4 text-mint shrink-0" strokeWidth={2} />}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}

          <div className="mt-10 flex flex-wrap gap-3">
            <button
              onClick={save}
              disabled={saving || selected.size === 0}
              data-testid="discover-save-button"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3 text-sm font-medium text-white/80 transition-colors duration-200 hover:border-mint/50 disabled:opacity-40"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} /> : <Save className="w-4 h-4" strokeWidth={1.5} />}
              {t("discover.saveCta")}
            </button>
            <button
              onClick={sendAll}
              disabled={sending || selected.size === 0}
              data-testid="discover-send-all-button"
              className="inline-flex items-center gap-2 rounded-full bg-mint px-6 py-3 text-sm font-semibold text-night transition-transform duration-200 hover:scale-105 disabled:opacity-40 disabled:hover:scale-100"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} /> : <Send className="w-4 h-4" strokeWidth={2} />}
              {sending ? t("discover.sending") : t("discover.sendAllCta")}
            </button>
          </div>
          <p className="mt-3 text-xs text-white/35">{t("discover.sendAllHint")}</p>
        </motion.div>
      </div>
      <Footer />
    </main>
  );
}
